import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiGet, apiPost, apiDelete } from "../lib/api";
import { CATEGORIES, categoryLabel } from "../lib/categories";
import { SizeField } from "../components/SizeField";
import { Alert, Button, Field, Input, Select, Spinner, EmptyState } from "../components/ui";

/* Saved alerts: "email me when someone lists cleats in 3Y".
   Browse links here with ?category=&size= prefilled from the active filters. */
export function AlertsPage() {
  const [params] = useSearchParams();
  const [alerts, setAlerts] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    category: params.get("category") || "any",
    size: params.get("size") || "",
    maxPrice: "",
  });

  async function load() {
    try {
      const { alerts } = await apiGet("/alerts");
      setAlerts(alerts);
    } catch (e) {
      setError(e.message || "Couldn't load your alerts.");
      setAlerts([]);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function create(e) {
    e.preventDefault();
    setError("");
    setNotice("");
    setSaving(true);
    try {
      await apiPost("/alerts", {
        category: form.category,
        size: form.size.trim(),
        maxPrice: form.maxPrice === "" ? null : Number(form.maxPrice),
      });
      setForm({ category: "any", size: "", maxPrice: "" });
      setNotice("Alert saved. We'll email you when something matches.");
      await load();
    } catch (e) {
      setError(e.message || "Couldn't save that alert.");
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    setBusyId(id);
    setError("");
    try {
      await apiDelete(`/alerts/${id}`);
      await load();
    } catch (e) {
      setError(e.message || "Couldn't delete that alert.");
    } finally {
      setBusyId("");
    }
  }

  function describe(a) {
    const cat = a.category === "any" ? "Any category" : categoryLabel(a.category);
    const bits = [cat];
    if (a.size) bits.push(`size ${a.size}`);
    if (a.maxPrice != null) bits.push(`under $${Number(a.maxPrice).toFixed(2)}`);
    return bits.join(" · ");
  }

  if (alerts === null) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-bold text-slate-800">Alerts</h1>
      <p className="mt-1 text-sm text-slate-500">
        Get an email when someone lists gear you're looking for.
      </p>

      {error && <div className="mt-4"><Alert>{error}</Alert></div>}
      {notice && <div className="mt-4"><Alert kind="success">{notice}</Alert></div>}

      <form onSubmit={create} className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="font-semibold text-slate-800">New alert</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Field label="Category">
            <Select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              <option value="any">Any category</option>
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          </Field>
          {form.category === "any" ? (
            <Field label="Size">
              <Input value="" disabled placeholder="Any size" />
            </Field>
          ) : (
            <SizeField
              category={form.category}
              value={form.size}
              onChange={(size) => setForm((f) => ({ ...f, size }))}
            />
          )}
          <Field label="Max price">
            <Input
              type="number"
              min="0"
              step="1"
              value={form.maxPrice}
              onChange={(e) => setForm({ ...form, maxPrice: e.target.value })}
              placeholder="Any price"
            />
          </Field>
        </div>
        <Button type="submit" disabled={saving} className="mt-3">
          {saving ? <Spinner /> : "Save alert"}
        </Button>
      </form>

      {alerts.length === 0 ? (
        <div className="mt-6">
          <EmptyState title="No alerts yet">
            Add one above and we'll email you the moment matching gear is listed.
          </EmptyState>
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white">
          {alerts.map((a) => (
            <li key={a.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-800">{describe(a)}</div>
                <div className="text-xs text-slate-400">
                  Emails go to your account address.
                </div>
              </div>
              <Button
                variant="secondary"
                disabled={busyId === a.id}
                onClick={() => remove(a.id)}
              >
                {busyId === a.id ? <Spinner /> : "Delete"}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
